import React, { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildingColumns,
  faFileMagnifyingGlass,
  faUser
} from '@fortawesome/pro-regular-svg-icons';
import { IUserDraft } from '@publishing-engine/domain/models';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Divider from '../../components/Divider';

interface Props {
  setCurrentStep: (step: number) => void;
  user?: IUserDraft | null | undefined;
  draftStep: number;
  scrollContainerRef: React.RefObject<HTMLElement>;
}

export const MenuSteps = [
  {
    label: 'Account Details',
    step: 1,
    icon: faUser,
    items: [
      { label: 'Personal Details', element: 'PersonalDetails' },
      { label: 'Password Details', element: 'PasswordDetails' }
    ]
  },
  {
    label: 'Affiliations',
    step: 2,
    icon: faBuildingColumns,
    items: [
      { label: 'Primary Affiliation', element: 'PrimaryAddress' },
      { label: 'Secondary Affiliation(s)', element: 'SecondaryAddress' }
    ]
  },
  {
    label: 'Research Interest',
    step: 3,
    icon: faFileMagnifyingGlass,
    items: [
      { label: 'Specialty & Keyword(s)', element: 'SpecialtyKeywords' },
      { label: 'Reviewer Interest', element: 'ReviewerInterest' }
    ]
  }
];

const SignUpMenu: React.FC<Props> = ({
  setCurrentStep,
  user,
  draftStep,
  scrollContainerRef
}) => {
  const [isActiveText, setIsActiveText] = useState<string>('');
  const activeDraftStep = user?.draftStep || 0;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const scrollToTop = () => window.scrollTo({ top: 0 });

  const handleStepChange = (label: string, step: number) => {
    setIsActiveText(label);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('draftStep', step.toString());
    navigate({ search: `?${nextSearchParams.toString()}` }, { replace: true });
    setCurrentStep(step);
  };

  const handleSectionClick = (
    elementId: string,
    label: string,
    step: number
  ) => {
    handleStepChange(label, step);
    const target = document.getElementById(elementId) as HTMLElement;
    if (!target || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const headerOffset = 120;
    const targetY = target.offsetTop - headerOffset;
    const maxScroll = container.scrollHeight - container.clientHeight;

    container.scrollTo({
      top: Math.max(0, Math.min(targetY, maxScroll)),
      behavior: 'smooth'
    });
  };

  return (
    <>
      {/* Mobile / Tablet: horizontal scrollable step indicator */}
      <Box display={{ base: 'block', md: 'none' }}>
        <Box overflowX="auto" pb={1} bgColor="surfacePrimaryFill">
          <Flex gap={0} minWidth="max-content" alignItems="stretch">
            {MenuSteps.map((step, index) => (
              <React.Fragment key={step.step}>
                <Flex
                  direction="column"
                  alignItems="center"
                  gap={1}
                  px={3}
                  py={2}
                  cursor={
                    step.step > activeDraftStep ? 'not-allowed' : 'pointer'
                  }
                  opacity={step.step > activeDraftStep ? 0.5 : 1}
                  borderBottom="3px solid"
                  borderColor={
                    step.step === draftStep
                      ? 'actionSecondaryContent'
                      : 'transparent'
                  }
                  color={
                    step.step === draftStep
                      ? 'actionSecondaryContent'
                      : undefined
                  }
                  onClick={() => {
                    if (step.step <= activeDraftStep) {
                      handleStepChange(step.label, step.step);
                      scrollToTop();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={step.icon} />
                  <Text
                    textStyle="textContext"
                    fontSize="xs"
                    whiteSpace="nowrap"
                    fontWeight={step.step === draftStep ? 'bold' : undefined}
                  >
                    {step.label}
                  </Text>
                </Flex>
                {index < MenuSteps.length - 1 && (
                  <Flex alignItems="center">
                    <Box w="20px" h="1px" bgColor="neutralPrimaryBorder" />
                  </Flex>
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Desktop: accordion sidebar */}
      <Flex
        backgroundColor="surfaceSecondaryFill"
        display={{ base: 'none', md: 'flex' }}
      >
        <Box
          height="calc(100vh)"
          bgColor="surfacePrimaryFill"
          p={3}
          overflowY="auto"
          borderRadius="10px"
        >
          <VStack align="stretch" spacing={2}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingItem">Create an Account</Text>
              {Divider}
            </Flex>

            <Accordion
              allowToggle
              defaultIndex={MenuSteps.map((_, idx) => idx)}
              allowMultiple
            >
              {MenuSteps.map(step => (
                <AccordionItem
                  key={step.step}
                  border="none"
                  isDisabled={step.step > activeDraftStep}
                >
                  {() => (
                    <>
                      <AccordionButton
                        aria-label={`Open ${step.label}`}
                        borderLeft={
                          step.step === draftStep ? '3px solid' : '3px solid'
                        }
                        borderColor={
                          step.step === draftStep
                            ? 'actionSecondaryContent'
                            : 'transparent'
                        }
                        onClick={() => {
                          handleStepChange(step.label, step.step);
                          scrollToTop();
                        }}
                        _disabled={{
                          opacity: 1,
                          cursor: 'not-allowed'
                        }}
                      >
                        <Flex
                          justifyContent="center"
                          alignItems="center"
                          gap={1}
                          whiteSpace="nowrap"
                          color={
                            step.step === draftStep
                              ? 'actionSecondaryContent'
                              : undefined
                          }
                        >
                          <FontAwesomeIcon icon={step.icon} />
                          <Text textStyle="headingItem">{step.label}</Text>
                        </Flex>
                      </AccordionButton>
                      <AccordionPanel pe={3}>
                        <Box
                          borderLeft="1px solid"
                          borderColor="neutralPrimaryBorder"
                          pl={3}
                        >
                          <VStack align="stretch" spacing={1}>
                            {step.items.map(item => (
                              <Box
                                key={item.element}
                                ml={2}
                                pb={1}
                                cursor={
                                  step.step > activeDraftStep
                                    ? 'not-allowed'
                                    : 'pointer'
                                }
                                _focus={{
                                  boxShadow: 'outline',
                                  bg: 'surfaceSecondaryFill'
                                }}
                                _hover={
                                  step.step > activeDraftStep
                                    ? {}
                                    : { bg: 'surfaceSecondaryFill' }
                                }
                                onClick={() => {
                                  if (step.step <= activeDraftStep) {
                                    handleSectionClick(
                                      item.element,
                                      item.label,
                                      step.step
                                    );
                                  }
                                }}
                              >
                                <Text
                                  textStyle={'textContext'}
                                  fontWeight={
                                    isActiveText === item.label
                                      ? 'bold'
                                      : undefined
                                  }
                                  opacity={
                                    step.step > activeDraftStep
                                      ? 0.8
                                      : 'initial'
                                  }
                                >
                                  {item.label}
                                </Text>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ))}
            </Accordion>
          </VStack>
        </Box>
      </Flex>
    </>
  );
};

export default SignUpMenu;
